import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import type { CustomerSegmentsOut } from '../api/types'
import { PageHeader } from '../components/Layout'
import { FilterBar } from '../components/FilterBar'
import { Kpi } from '../components/Kpi'
import { Pager } from '../components/Pager'
import { SearchBox } from '../components/SearchBox'
import { Panel } from '../components/States'
import { SortHeader, useSort } from '../components/SortHeader'
import { useFilters, useParam, usePaging } from '../lib/filters'
import { count, formatDate, money, percent } from '../lib/format'

const SEGMENTS = ['top', 'repeat', 'one_time', 'dormant'] as const
type Segment = (typeof SEGMENTS)[number]

const SEGMENT_LABELS: Record<Segment, string> = {
  top: 'Top spenders',
  repeat: 'Repeat buyers',
  one_time: 'One-time buyers',
  dormant: 'Dormant',
}

const SORTS = [
  'spend',
  'orders',
  'avg_order',
  'name',
  'first_purchase',
  'last_purchase',
] as const
type Sort = (typeof SORTS)[number]

export function CustomersPage() {
  const { filters } = useFilters()
  const { offset, limit, setOffset } = usePaging(25)
  const [segment, setSegment] = useParam<Segment>('segment', 'top', SEGMENTS)
  const { sort, direction, sortBy } = useSort<Sort>(SORTS, 'spend')
  const [dormantDays, setDormantDays] = useParam<string>('dormant_days', '90')
  const [params, setParams] = useSearchParams()
  const search = params.get('q') ?? ''

  function setSearch(next: string) {
    const updated = new URLSearchParams(params)
    if (next) updated.set('q', next)
    else updated.delete('q')
    updated.delete('offset')
    setParams(updated, { replace: true })
  }

  const query = useQuery({
    queryKey: [
      'customers',
      filters.from,
      filters.to,
      filters.outletId,
      filters.scope,
      segment,
      sort,
      direction,
      dormantDays,
      search,
      offset,
      limit,
    ],
    queryFn: () =>
      api.get<CustomerSegmentsOut>('/analytics/customers', {
        from_date: filters.from,
        to_date: filters.to,
        outlet_id: filters.outletId,
        scope: filters.scope,
        segment,
        sort,
        direction,
        dormant_days: Number(dormantDays) || 90,
        search: search || null,
        limit,
        offset,
      }),
  })

  const rows = query.data?.items ?? []
  const summary = query.data?.summary
  const sortProps = { sort, direction, onSort: sortBy } as const

  return (
    <>
      <PageHeader
        title="Customer insights"
        subtitle={`${formatDate(filters.from)} – ${formatDate(filters.to)}`}
      />

      <div className="content stack">
        <FilterBar />

        {summary ? (
          <div className="grid cols-4">
            <Kpi label="Active customers" value={count(summary.active_customers)} />
            <Kpi
              label="Repeat buyers"
              value={count(summary.repeat_count)}
              foot={`${percent(summary.repeat_rate_pct)} of active`}
            />
            <Kpi label="One-time buyers" value={count(summary.one_time_count)} />
            <Kpi
              label="Dormant"
              value={count(summary.dormant_count)}
              foot={`no purchase in ${dormantDays} days`}
            />
          </div>
        ) : null}

        <div className="row" style={{ alignItems: 'flex-end' }}>
          <SearchBox
            id="customer-search"
            label="Search customers"
            placeholder="Name or phone — e.g. barrett, 5551001"
            value={search}
            onChange={setSearch}
          />
        </div>

        <div className="row">
          <div className="seg">
            {SEGMENTS.map((option) => (
              <button
                key={option}
                className={segment === option ? 'on' : ''}
                onClick={() => setSegment(option)}
              >
                {SEGMENT_LABELS[option]}
              </button>
            ))}
          </div>

          {segment === 'dormant' ? (
            <div className="field">
              <label htmlFor="dormant">Dormant after (days)</label>
              <input
                id="dormant"
                type="number"
                min={7}
                max={730}
                style={{ width: 90 }}
                value={dormantDays}
                onChange={(event) => setDormantDays(event.target.value)}
              />
            </div>
          ) : null}
        </div>

        <div className="card">
          <div className="card-head">
            <h2>{SEGMENT_LABELS[segment]}</h2>
            <span className="hint">
              {count(query.data?.total)} customers{search ? ` matching “${search}”` : ''}
            </span>
          </div>
          <div className="card-body flush">
            <Panel
              isLoading={query.isPending}
              error={query.error}
              isEmpty={rows.length === 0}
              emptyText="No customers in this segment for the period."
            >
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <SortHeader label="Customer" sortKey="name" {...sortProps} />
                      <th>Phone</th>
                      <SortHeader label="Total spend" sortKey="spend" numeric {...sortProps} />
                      <SortHeader label="Orders" sortKey="orders" numeric {...sortProps} />
                      <SortHeader
                        label="Average order"
                        sortKey="avg_order"
                        numeric
                        {...sortProps}
                      />
                      <SortHeader label="First" sortKey="first_purchase" numeric {...sortProps} />
                      <SortHeader label="Last" sortKey="last_purchase" numeric {...sortProps} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.customer_id}>
                        <td>
                          <Link to={`/customers/${row.customer_id}`}>{row.name}</Link>
                          {row.hike_customer_id ? (
                            <div className="cell-sub">HIKE {row.hike_customer_id}</div>
                          ) : null}
                        </td>
                        <td className="cell-sub">{row.phone ?? '—'}</td>
                        <td className="num strong">{money(row.total_spend)}</td>
                        <td className="num">{count(row.order_count)}</td>
                        <td className="num">{money(row.avg_order_value)}</td>
                        <td className="num cell-sub">{formatDate(row.first_purchase)}</td>
                        <td className="num cell-sub">{formatDate(row.last_purchase)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager offset={offset} limit={limit} total={query.data?.total ?? 0} onChange={setOffset} />
            </Panel>
          </div>
        </div>
      </div>
    </>
  )
}
